/** zh-CN catalogue `pec` (W3-09; English: src/formats/pec/pec.messages.ts; terms: docs/i18n/glossary.md). */
import type { MessageCatalog } from '../../core/util/types';

const CHANNEL = '{channel, select, alpha {不透明度} x {X 位置} y {Y 位置} rotate {旋转} other {{channel}}}';

const catalog: MessageCatalog = {
  ns: 'pec',
  locale: 'zh-CN',
  messages: {
    'report.bom': '谱面文件以字节顺序标记（BOM）开头。已忽略它；带有它时 Phira 无法读取偏移。',
    'report.offsetMissing': '谱面的第一行应为以毫秒为单位的偏移，但它是空的。已使用 150（无偏移）。',
    'report.offsetInvalid': '谱面的第一行应为以毫秒为单位的偏移，但写的是“{token}”。已使用 150（无偏移）。',
    'report.offsetExtra': '谱面的第一行除偏移外还有其他内容（“{token}”）。已忽略其余部分；Phira 会拒绝这样的谱面。',
    'report.unknownCommand': '第 {sourceLine} 行：“{token}”不是 PEC 命令。已跳过该行。',
    'report.missingArgument': '第 {sourceLine} 行：命令缺少一个值。已跳过该行。',
    'report.expectedNumber': '第 {sourceLine} 行：“{token}”不是数字。已跳过该行。',
    'report.expectedIndex': '第 {sourceLine} 行：“{token}”不是大于或等于 0 的整数。已跳过该行。',
    'report.expectedEasing': '第 {sourceLine} 行：缓动“{token}”不是 0 到 255 之间的整数。已跳过该行。',
    'report.expected01': '第 {sourceLine} 行：假键标记“{token}”必须是 0 或 1。已跳过该行。',
    'report.unexpectedExtra': '第 {sourceLine} 行：已忽略命令后的“{token}”。Phira 会拒绝带有多余值的行。',
    'report.bpAfterCommands':
      '第 {sourceLine} 行：BPM 变化（bp）出现在音符或事件之后。仍已使用；Phira 要求所有 bp 行都在最前面。',
    'report.noNotesInserted': '第 {sourceLine} 行：速度（#）或尺寸（&）行出现在任何音符之前。已跳过该行。',
    'report.noBpm': '谱面没有 BPM（bp）行，这会使 Phira 崩溃。已使用 120 BPM。',
    'report.missingChannel': `一条判定线完全没有${CHANNEL}值，这会使 Phira 崩溃。已保持未设置。`,
    'report.missingSpeed': '一条判定线完全没有流速（cv）值，这会使 Phira 崩溃。其音符不会移动。',
    'report.motionBeforeValue': `第 {sourceLine} 行：一个${CHANNEL}运动出现在任何起始值之前，Phira 会拒绝这种情况。已从一开始就使用其目标值。`,
    'report.lineIndexTooLarge':
      '第 {sourceLine} 行：判定线编号 {token} 大于 {max}，这是编辑器能导入的最大值（每个编号都会创建直到该编号的所有判定线）。已跳过该行；Phira 会加载它。',
    'report.invalidBpm': '第 {sourceLine} 行：BPM {bpm} 不是正数。已忽略该行。',
    'report.bpmUnsorted': 'BPM 变化没有按顺序排列。已将其排序，并按 Phira 的计算方式经秒数换算了所有时间。',
    'report.reversedMotion': '第 {sourceLine} 行：一个运动的结束早于开始。已将其改为瞬间完成。',
    'report.unsortedSpeed': '流速变化（cv）没有按时间顺序排列。已按 Phira 正向播放的方式转换。',
    'report.speedJump': '顺序错乱的流速变化会让音符距离在 Phira 中跳变。这种跳变无法用流速表示，已被平滑处理。',
    'report.negativeHold': '第 {sourceLine} 行：一个 Hold 音符的结束早于开始。已将其长度设为零。',
    'report.visibleTimeInexact':
      'PE 的 alpha 代码会让部分音符在被击打前先显示、再隐藏、再显示。可见时间只保留每次击打前的最后一段。',
    'report.alphaCrossing': '一次淡入淡出在非整拍的时间经过了 PE 的 alpha 代码。已将这些变化放在最近的拍上。',
    'report.inexactTimes': '部分时间无法用拍数精确表示，已取整。',
    'report.timeOutOfRange': '部分拍数过大或过早于开头，谱面无法容纳。已移到谱面能容纳的最近拍数。',
    'report.bpmShifted':
      '第一个 BPM 变化位于第 {beat} 拍而不是第 0 拍。已平移所有时间使其位于第 0 拍；Phira 播放效果相同。',
    'report.bpmDuplicate': '有两个 BPM 变化位于同一拍。已保留后一个，与 Phira 的做法一致。',
    'report.overlapClipped': `重叠的${CHANNEL}事件已按 Phira 的方式裁剪。`,
    'report.alphaCodes':
      '判定线使用了 PE 的 alpha 代码（负的不透明度）。已将其转换为 Phira 显示效果相同的不透明度和音符可见性。',
    'report.visibleTime': '音符根据 PE 的 alpha 代码获得了可见时间，使它们在被击打前不久出现。',
    'report.beatsSnapped':
      '写成小数的拍数（例如 54.984）已按其代表的精确分数（6873/125）存储。Phira 以 32 位数字读取它们，无法区分两者；时间最多移动 {maxUs} 微秒。',
    'report.lineShape':
      '判定线的长度被设为 65.17%，粗细被设为 1.0002，因为 Phira 绘制 PhiEditer 判定线比 RPE 判定线更短更细。',
    'report.holdTailFrozen':
      '一个 Hold 在最后一个音符或事件之后超过一秒才结束。Phira 会在那里停止判定线的运动，因此 Hold 的结尾会画得更短；导入时保留这一行为。',
  },
};

export default catalog;
